import { useCallback, useRef } from 'react';

type AlertTier = 'critical' | 'newMission' | 'statusChange';

function playTone(
  frequency: number,
  duration: number,
  type: OscillatorType = 'sine',
  volume: number = 0.3,
  repeat: number = 1,
  gap: number = 150
) {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    for (let i = 0; i < repeat; i++) {
      const startTime = ctx.currentTime + (i * (duration + gap)) / 1000;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = type;
      osc.frequency.value = frequency;
      gain.gain.value = volume;
      gain.gain.setValueAtTime(volume, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + duration / 1000);
      osc.start(startTime);
      osc.stop(startTime + duration / 1000);
    }
  } catch (e) {
    console.warn('Audio playback failed:', e);
  }
}

export function useAudioAlert() {
  const audioUnlocked = useRef(false);

  const unlock = useCallback(() => {
    // Unlock audio context
    if (!audioUnlocked.current) {
      try {
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        ctx.resume().then(() => { audioUnlocked.current = true; ctx.close(); });
      } catch {}
    }
    // Request notification permission
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const playAlert = useCallback((tier: AlertTier, muted: boolean) => {
    if (muted || tier === 'statusChange') return;

    // Audio chime when tab is active
    if (document.visibilityState === 'visible') {
      if (tier === 'critical') playTone(880, 300, 'square', 0.25, 3, 200);
      else if (tier === 'newMission') playTone(660, 200, 'sine', 0.2, 2, 150);
    }

    // Browser notification when tab is backgrounded (or always for critical)
    if ('Notification' in window && Notification.permission === 'granted') {
      if (document.visibilityState === 'hidden' || tier === 'critical') {
        const titles: Record<AlertTier, string> = {
          critical: 'CRITICAL MISSION',
          newMission: 'New Mission Received',
          statusChange: '',
        };
        try {
          new Notification(titles[tier], {
            icon: '/pwa-192x192.png',
            tag: `hems-${tier}-${Date.now()}`,
            requireInteraction: tier === 'critical',
          });
        } catch {}
      }
    }
  }, []);

  return { playAlert, unlock };
}
